export interface FilterTemplate {
    key: string
    operator: FilterBy['operator']
    placeholder?: string
    /** Validates and normalizes a committed value, throwing a user-facing error when invalid. */
    parseValue?: (value: string) => { value: string; displayName: string }
    possibleValues?: (searchText: string) => Promise<{ value: string; displayName: string }[]>
    possibleValuesSync?: (searchText?: string) => { value: string; displayName: string }[]
}

export type FilterBy = IsFilter | IsOneOfFilter | MatchesFilter | DateFilter

export interface IsOneOfFilter {
    key: string // e.g. appliance
    operator: 'isOneOf'
    values: { value: string; displayName: string }[]
}

export interface IsFilter {
    key: string // e.g. tag
    operator: 'is'
    value: string
    displayName?: string
}

export interface MatchesFilter {
    key: string // e.g. name
    operator: 'matches'
    value: string
    displayName?: string
}

export interface DateFilter {
    key: string // e.g. 1970-01-01T00:00:00.000Z
    operator: 'after' | 'before'
    value: string
    displayName?: string
}

export function urlParamsToFilters<T extends object>(
    urlParams: T,
    mapUrlParamToFilterFn: (key: keyof T, value: string) => FilterBy | undefined
): FilterBy[] {
    const filters: FilterBy[] = []
    for (const key of Object.keys(urlParams) as Array<keyof typeof urlParams>) {
        if (!urlParams[key]) {
            continue
        }
        const filter = mapUrlParamToFilterFn(key, urlParams[key] as string)
        if (filter) {
            filters.push(filter)
        }
    }
    return filters
}

/** Encodes the new filters and clears URL keys owned by removed filters. */
export function filtersToUrlParams<T extends object>(
    filters: FilterBy[],
    prevFilters: FilterBy[],
    mapFilterToUrlParamFn: (filter: FilterBy) => Partial<T>
): Partial<T> {
    const encode = (values: FilterBy[]) => Object.assign({}, ...values.map(mapFilterToUrlParamFn)) as Partial<T>
    const next = encode(filters)
    const previous = encode(prevFilters)
    for (const key of Object.keys(previous) as (keyof T)[]) {
        if (!(key in next)) next[key] = '' as T[keyof T]
    }
    return next
}

/**
 * Merges a single incoming filter into the currently applied filters, keyed by
 * (key, operator). Multi-value `isOneOf` filters gain the new values (de-duped);
 * single-value filters are replaced. Returns the original array reference
 * unchanged when the incoming filter adds nothing new, so callers can detect a
 * no-op.
 */
export function mergeFilter(current: FilterBy[], incoming: FilterBy): FilterBy[] {
    const existing = current.find((f) => f.key === incoming.key && f.operator === incoming.operator)
    if (!existing) return [...current, incoming]

    if (existing.operator === 'isOneOf' && incoming.operator === 'isOneOf') {
        const additions = incoming.values.filter((v) => !existing.values.some((e) => e.value === v.value))
        if (additions.length === 0) return current
        const updated: IsOneOfFilter = { ...existing, values: [...existing.values, ...additions] }
        return current.map((f) => (f === existing ? updated : f))
    }

    if ('value' in existing && 'value' in incoming && existing.value === incoming.value) return current
    return current.map((f) => (f === existing ? incoming : f))
}
